import { createContext, createElement, useContext, useRef, type ReactNode } from 'react';
import { createStore } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

interface MediaPlayerState {
  mediaElement: HTMLVideoElement | null;
}

interface MediaPlayerActions {
  registerMediaElement: (el: HTMLVideoElement | null) => void;
  seekMedia: (seconds: number) => void;
  pauseMedia: () => void;
  getCurrentTime: () => number | null;
}

type MediaPlayerStore = MediaPlayerState & MediaPlayerActions;

// Per-pane factory: each AssetPreview subtree (single-asset view, or one
// pane of the compare-versions view) gets its own media-element registry so
// playback / seek / pause never crosses pane boundaries.
function createMediaPlayerStore() {
  return createStore<MediaPlayerStore>((set, get) => ({
    mediaElement: null,
    registerMediaElement: (el) => {
      if (get().mediaElement === el) return;
      if (el === null && get().mediaElement !== null) return;
      set({ mediaElement: el });
    },
    seekMedia: (seconds) => {
      const el = get().mediaElement;
      if (el && Number.isFinite(seconds)) el.currentTime = seconds;
    },
    pauseMedia: () => {
      const el = get().mediaElement;
      if (el && !el.paused) el.pause();
    },
    getCurrentTime: () => get().mediaElement?.currentTime ?? null,
  }));
}

type MediaPlayerStoreApi = ReturnType<typeof createMediaPlayerStore>;

const MediaPlayerContext = createContext<MediaPlayerStoreApi | null>(null);

export function MediaPlayerProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<MediaPlayerStoreApi | null>(null);
  if (!storeRef.current) storeRef.current = createMediaPlayerStore();
  return createElement(MediaPlayerContext.Provider, { value: storeRef.current }, children);
}

function useMediaPlayerStoreApi(): MediaPlayerStoreApi {
  const ctx = useContext(MediaPlayerContext);
  if (!ctx) throw new Error('useMediaPlayer must be used inside <MediaPlayerProvider>');
  return ctx;
}

export const useMediaPlayer = () => {
  const store = useMediaPlayerStoreApi();
  return useStoreWithEqualityFn(
    store,
    (s) => ({
      mediaElement: s.mediaElement,
      registerMediaElement: s.registerMediaElement,
      seekMedia: s.seekMedia,
      pauseMedia: s.pauseMedia,
      getCurrentTime: s.getCurrentTime,
    }),
    shallow,
  );
};

const NO_PROVIDER_MEDIA_PLAYER: MediaPlayerStore = {
  mediaElement: null,
  registerMediaElement: () => {},
  seekMedia: () => {},
  pauseMedia: () => {},
  getCurrentTime: () => null,
};

/**
 * Like `useMediaPlayer`, but tolerates a missing `<MediaPlayerProvider>` by
 * returning inert no-ops instead of throwing. Used by `AnnotatedCommentInput`
 * so the same composer can render on surfaces that have no media player
 * (e.g. brief comments) without forcing every caller to mount a provider.
 * Asset surfaces still mount the provider, so their behavior is unchanged.
 *
 * Hook order is stable: `useContext` and `useStoreWithEqualityFn` are always
 * called; the latter subscribes to a throwaway store created once when no
 * provider is present.
 */
const FALLBACK_STORE = createMediaPlayerStore();
export const useMediaPlayerOptional = () => {
  const ctx = useContext(MediaPlayerContext);
  const store = ctx ?? FALLBACK_STORE;
  const value = useStoreWithEqualityFn(
    store,
    (s) => ({
      mediaElement: s.mediaElement,
      registerMediaElement: s.registerMediaElement,
      seekMedia: s.seekMedia,
      pauseMedia: s.pauseMedia,
      getCurrentTime: s.getCurrentTime,
    }),
    shallow,
  );
  return ctx ? value : NO_PROVIDER_MEDIA_PLAYER;
};
